import { useState, useEffect, useRef } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Stage } from "../../../types/productTypes";
import { useTranslation } from "@/hooks/use-translation";

interface HorizontalStepperProps {
   stages: Stage[];
   index: number;
   setActiveStep: (index: number) => void;
}

export default function HorizontalStepper({
   stages,
   index,
   setActiveStep,
}: HorizontalStepperProps) {
   const totalSteps = stages.length;
   const { translate } = useTranslation();
   // Compute the start index of the final batch of up to 4 items:
   // e.g. if totalSteps = 10 → floor((10 − 1)/4) * 4 = 2 * 4 = 8.
   const lastBatchStart = Math.floor((totalSteps - 1) / 4) * 4;

   const [startIndex, setStartIndex] = useState(0);
   const prevStartRef = useRef(0);

   useEffect(() => {
      // Determine which batch of four the active index belongs to:
      const batchStart = Math.floor(index / 4) * 4;

      // Clamp so we never exceed the start of that final, possibly partial batch:
      const desiredStart = Math.min(batchStart, lastBatchStart);

      // Only update if it changed
      if (desiredStart !== startIndex) {
         prevStartRef.current = startIndex;
         setStartIndex(desiredStart);
      }
   }, [index, lastBatchStart, startIndex]);

   // If startIndex increased, new group is to the right → slide left (direction = -1).
   // Else slide right (direction = +1).
   const direction = startIndex > prevStartRef.current ? -1 : 1;

   // Slice out up to four items. If there are fewer than 4 remaining
   // (i.e. at the final batch), this will return only those remaining.
   const visibleStages = stages.slice(startIndex, startIndex + 4);
   return (
      <div className="w-full bg-white border-b border-gray-200  overflow-hidden">
         {/* Fixed-height container to prevent vertical jumps during animation */}
         <div className="relative h-[3rem] w-full">
            <AnimatePresence initial={false} custom={direction}>
               <motion.div
                  key={startIndex}
                  custom={direction}
                  className="absolute flex items-center justify-center space-x-4"
                  initial={{ x: -direction * 200, opacity: 0 }}
                  animate={{ x: 0, opacity: 1 }}
                  transition={{
                     type: "spring",
                     stiffness: 400,
                     damping: 30,
                  }}
               >
                  {visibleStages.map((stage, i) => {
                     const absoluteIndex = startIndex + i;
                     return (
                        <div key={absoluteIndex} className="flex items-center">
                           <motion.button
                              className={`relative text-m-body-s-line mr-4 py-2 transition-colors duration-300 ${
                                 absoluteIndex === index
                                    ? "text-gray-900 font-medium"
                                    : absoluteIndex < index
                                    ? "text-gray-600"
                                    : "text-gray-400"
                              }`}
                              onClick={() => setActiveStep(absoluteIndex)}
                              whileHover={{ scale: 1.05 }}
                              whileTap={{ scale: 0.95 }}
                           >
                              {translate(stage)}
                              {absoluteIndex === index && (
                                 <motion.div
                                    className="absolute bottom-0 left-0 right-0 h-0.5 bg-gray-900"
                                    layoutId="underline"
                                    initial={{ opacity: 0 }}
                                    animate={{ opacity: 1 }}
                                    transition={{
                                       type: "spring",
                                       stiffness: 500,
                                       damping: 30,
                                    }}
                                 />
                              )}
                           </motion.button>

                           {i < visibleStages.length - 1 && (
                              <motion.span
                                 className="text-gray-300 text-xl mx-2"
                                 initial={{ opacity: 0.5 }}
                                 animate={{
                                    opacity: absoluteIndex < index ? 0.8 : 0.3,
                                 }}
                                 transition={{ duration: 0.3 }}
                              >
                                 /
                              </motion.span>
                           )}
                        </div>
                     );
                  })}
               </motion.div>
            </AnimatePresence>
         </div>
      </div>
   );
}
